import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Alert,
  AlertTitle,
  Chip,
  Typography,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  CheckCircle as ValidIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import ReactJson from 'react-json-view';
import api from '../../services/api';

/**
 * Rule JSON Editor Component
 *
 * Provides JSON editing for triage strategy rules with real-time validation
 */
const RuleJsonEditor = ({ rules, onChange, strategyId }) => {
  const [validationState, setValidationState] = useState({
    isValidating: false,
    errors: [],
    warnings: [],
    isValid: null
  });

  // Debounced validation
  const validateRules = useCallback(async (rulesToValidate) => {
    if (!rulesToValidate || !Array.isArray(rulesToValidate)) {
      setValidationState({
        isValidating: false,
        errors: ['Rules must be a valid array'],
        warnings: [],
        isValid: false
      });
      return;
    }

    setValidationState(prev => ({ ...prev, isValidating: true }));

    try {
      const response = await api.post(
        `/admin/triage-strategies/${strategyId}/validate-rules`,
        { rules: rulesToValidate }
      );

      setValidationState({
        isValidating: false,
        errors: response.data.errors || [],
        warnings: response.data.warnings || [],
        isValid: response.data.valid
      });
    } catch (error) {
      console.error('Validation error:', error);
      setValidationState({
        isValidating: false,
        errors: [error.response?.data?.msg || 'Validation failed'],
        warnings: [],
        isValid: false
      });
    }
  }, [strategyId]);

  // Validate on mount and when rules change (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      validateRules(rules);
    }, 500); // Debounce for 500ms

    return () => clearTimeout(timer);
  }, [rules, validateRules]);

  const handleEdit = (edit) => {
    // react-json-view returns the updated JSON in edit.updated_src
    if (edit.updated_src && Array.isArray(edit.updated_src)) {
      onChange(edit.updated_src);
    }
  };

  const handleAdd = (add) => {
    if (add.updated_src && Array.isArray(add.updated_src)) {
      onChange(add.updated_src);
    }
  };

  const handleDelete = (del) => {
    if (del.updated_src && Array.isArray(del.updated_src)) {
      onChange(del.updated_src);
    }
  };

  return (
    <Box>
      {/* Validation Status */}
      <Box sx={{ mb: 2 }}>
        {validationState.isValidating && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Validating rules...
            </Typography>
          </Box>
        )}

        {!validationState.isValidating && validationState.isValid === true && (
          <Alert severity="success" icon={<ValidIcon />}>
            <AlertTitle>Valid Rules</AlertTitle>
            All rules validated successfully. {rules.length} rule{rules.length !== 1 ? 's' : ''} configured.
          </Alert>
        )}

        {!validationState.isValidating && validationState.isValid === false && (
          <Alert severity="error" icon={<ErrorIcon />}>
            <AlertTitle>Validation Errors</AlertTitle>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {validationState.errors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </Box>
          </Alert>
        )}

        {!validationState.isValidating && validationState.warnings.length > 0 && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 1 }}>
            <AlertTitle>Warnings</AlertTitle>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {validationState.warnings.map((warning, idx) => (
                <li key={idx}>{warning}</li>
              ))}
            </Box>
          </Alert>
        )}
      </Box>

      {/* Rule Summary */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label={`${rules.length} Rules`}
          color="primary"
          size="small"
        />
        <Chip
          label={`Priorities: ${rules.map(r => r.priority).join(', ')}`}
          size="small"
          variant="outlined"
        />
      </Box>

      {/* JSON Editor */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          maxHeight: '600px',
          overflow: 'auto',
          backgroundColor: '#f5f5f5'
        }}
      >
        <ReactJson
          src={rules}
          name="rules"
          theme="rjv-default"
          iconStyle="triangle"
          displayDataTypes={false}
          displayObjectSize={true}
          enableClipboard={true}
          collapsed={2}
          onEdit={handleEdit}
          onAdd={handleAdd}
          onDelete={handleDelete}
          style={{
            backgroundColor: '#f5f5f5',
            padding: '10px',
            borderRadius: '4px',
            fontSize: '13px',
            fontFamily: 'Monaco, monospace'
          }}
        />
      </Paper>

      {/* Helper Info */}
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Editing Tips:</strong>
        </Typography>
        <Box component="ul" sx={{ m: 0, mt: 1, pl: 2 }}>
          <li>Click on values to edit them inline</li>
          <li>Use the + icon to add new fields or array items</li>
          <li>Use the - icon to delete fields</li>
          <li>Rules are automatically validated as you edit</li>
          <li>Priorities must be unique positive integers</li>
        </Box>
      </Alert>
    </Box>
  );
};

export default RuleJsonEditor;
